import { useEffect } from "react";
import { caseMethods, transformKeys } from "../api/case";
import { SSE_HOST } from "../env";

let eventSource: EventSource | null = null;

type Notification = {
  payload:
    | {
        event: "inbound_sms_message_created";
        contactId: string;
        contact: {
          firstName: string;
          lastName: string;
        };
        body: string;
      }
    | {
        event: "milestone_message_created";
        userId: string;
        milestone: {
          name: string;
        };
        contact: {
          firstName: string;
          lastName: string;
        };
      }
    | {
        event: "team_milestone_created";
        user: {
          id: string;
          firstName: string;
          lastName: string;
        };
        contact: {
          id: string;
          firstName: string;
          lastName: string;
        };
        milestone: {
          id: string;
          name: string;
        };
      }
    | {
        event: "provider-phone-number-updated";
        object: {
          label: string;
          provider: string;
          avatar_url: string;
          deleted_at: string | null;
          phone_number: string;
          auto_responder_text: string;
          auto_responder_enabled: boolean;
          call_forward_phone_number: string | null;
        };
        changes:
          | {
              label?: string[];
              provider?: string[];
              avatar_url?: string[];
              deleted_at?: (string | null)[];
              phone_number: string[];
              auto_responder_text?: string[];
              auto_responder_enabled?: boolean[];
              call_forward_phone_number?: (string | null)[];
            }
          | {
              event: "provider-phone-number-deleted";
              object: {
                label: string;
                provider: string;
                avatar_url: string;
                deleted_at: string | null;
                phone_number: string;
                auto_responder_text: string;
                auto_responder_enabled: boolean;
                call_forward_phone_number: string | null;
              };
            };
      };
};

type Handler = (notification: Notification) => void;

type SSEEvent = Event & {
  data: any;
};

export function useSSE(event: "notification", handler: Handler) {
  function handlerWrapper(event: SSEEvent) {
    let notification = JSON.parse(event.data);
    if (typeof notification.payload === "string") {
      try {
        notification.payload = JSON.parse(notification.payload);
      } catch (_) {}
    }
    notification = transformKeys(notification, caseMethods.camel);
    handler(notification);
  }

  useEffect(() => {
    async function init() {
      if (!eventSource) await initEventSource();
      await subscribe(event, handlerWrapper);
    }

    init();

    return () => {
      unsubscribe(event, handlerWrapper);
    };
  });
}

async function initEventSource() {
  eventSource = new EventSource(SSE_HOST, { withCredentials: true });
}

async function subscribe(event: string, handler: any) {
  eventSource!.addEventListener(event, handler);
}

async function unsubscribe(event: string, handler: any) {
  eventSource!.removeEventListener(event, handler);
}
